import { errorWrapper } from 'utility/error';

import { apiClient } from './api';
import { toActivityDomain } from './transformers/toActivityDomain';
import { toActivityTypeDomain } from './transformers/toActivityTypeDomain';
import { toAreaDomain } from './transformers/toAreaDomain';
import { toBranchDomain } from './transformers/toBranchDomain';
import { toDistributionCenterDomain } from './transformers/toDistributionCenterDomain';
import { toEmployeeDomain } from './transformers/toEmployeeDomain';
import { toProductivityGoalDomain } from './transformers/toProductivityGoalDomain';
import { toSubZoneDomain } from './transformers/toSubZoneDomain';
import { toUnitOfMeasureDomain } from './transformers/toUnitOfMeasureDomain';
import { toZoneDomain } from './transformers/toZoneDomain';

// Fetches a tenant by id and maps its sub-collections onto our normalized
// `Data.*` domains. `boundaries` is intentionally skipped for now — the
// corresponding `Data.Boundary` interface doesn't exist yet.
async function getTenant(args: { tenantId: string }) {
  const { tenantId } = args;

  const apiCall = apiClient.Tenant_Get({
    params: { tenantId },
  });

  return await errorWrapper({
    apiCall,
    error: { message: '', severity: 'error', title: 'Failed to load tenant' },
    onSuccess: (tenant) => {
      const {
        activities,
        activityTypes,
        areas,
        branches,
        distributionCenters,
        employees,
        subZones,
        unitsOfMeasure,
        zones,
      } = tenant;

      // Productivity goals live inline on both zones and subzones. Flatten
      // them into a single list so the productivity-target domain holds
      // every target across the tenant, regardless of which parent carried
      // the goal.
      const productivityGoals = [...zones, ...subZones].flatMap(
        (item) => item.productivityGoals ?? [],
      );

      const { activityDomain } = toActivityDomain({ dtos: activities });
      const { activityTypeDomain } = toActivityTypeDomain({
        dtos: activityTypes,
      });
      const { areaDomain } = toAreaDomain({ dtos: areas });
      const { branchDomain } = toBranchDomain({ dtos: branches });
      const { distributionCenterDomain } = toDistributionCenterDomain({
        dtos: distributionCenters,
      });
      const { employeeDomain } = toEmployeeDomain({ dtos: employees });
      const { productivityTargetDomain } = toProductivityGoalDomain({
        dtos: productivityGoals,
      });
      const { subZoneDomain } = toSubZoneDomain({ dtos: subZones });
      const { unitOfMeasureDomain } = toUnitOfMeasureDomain({
        dtos: unitsOfMeasure,
      });
      const { zoneDomain } = toZoneDomain({ dtos: zones });
      return {
        activityDomain,
        activityTypeDomain,
        areaDomain,
        branchDomain,
        distributionCenterDomain,
        employeeDomain,
        productivityTargetDomain,
        subZoneDomain,
        unitOfMeasureDomain,
        zoneDomain,
      };
    },
  });
}

export { getTenant };
